import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { axiosInstance } from 'hooks/auth/AxiosInstance';
import { AxiosResponse } from 'axios';

export interface NTSASummary {
  totalFines: number;
  smsSent: number;
  smsNotSent: number;
  smsPending: number;
  smsFailed: number;
  cameraType?: string;
}

export interface NTSASummaryParams {
  from: string;
  to: string;
  cameraType?: string;
}

export const normalizeNTSASummary = (
  data: Partial<NTSASummary> = {},
): NTSASummary => {
  const totalFines = data.totalFines ?? 0;
  const smsSent = data.smsSent ?? 0;
  const smsPending = data.smsPending ?? 0;
  const smsFailed = data.smsFailed ?? 0;

  return {
    totalFines,
    smsSent,
    smsNotSent: data.smsNotSent ?? Math.max(0, totalFines - smsSent),
    smsPending,
    smsFailed,
    ...(data.cameraType ? { cameraType: data.cameraType } : {}),
  };
};

const fetchNTSASummary = async (
  params: NTSASummaryParams,
): Promise<NTSASummary> => {
  const queryParams = new URLSearchParams({
    from: params.from,
    to: params.to,
  });

  if (params.cameraType) {
    queryParams.set('cameraType', params.cameraType);
  }

  const response: AxiosResponse<Partial<NTSASummary>> = await axiosInstance.get(
    `/reports/summary?${queryParams.toString()}`,
  );

  return normalizeNTSASummary(response.data);
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useGetNTSASummary = (
  params: NTSASummaryParams,
): UseQueryResult<NTSASummary, Error> => {
  return useQuery<NTSASummary, Error>({
    queryKey: ['ntsa-summary', params.from, params.to, params.cameraType],
    queryFn: () => fetchNTSASummary(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!params.from && !!params.to,
  });
};

export default useGetNTSASummary;
